import type { ActionKey, AuthUser, ModuleKey } from '@fluxus/shared';
import {
  Activity,
  BellRing,
  CalendarClock,
  Presentation as PresentationIcon,
  Stamp,
  Building2,
  ClipboardCheck,
  FileSearch,
  ClipboardList,
  Compass,
  Flag,
  Gauge,
  GitBranch,
  Grid3x3,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  LayoutGrid,
  ListChecks,
  MonitorSmartphone,
  Network,
  Rocket,
  ShieldCheck,
  Settings2,
  SlidersHorizontal,
  CalendarCheck,
  CalendarRange,
  FileSpreadsheet,
  FileClock,
  FileBarChart,
  TrendingUp,
  Wallet,
  Store,
  ChartLine,
  History as HistoryIcon,
  Target,
  Trophy,
  UsersRound,
  Waypoints,
} from 'lucide-react';
import { alertOpenCountSchema, SSO_RETURN_PATH } from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, type RouteObject } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { can, canAccess, homePathFor } from '@/lib/auth/access';
import { useAuth, useCurrentUser } from '@/lib/auth/session';
import { ForbiddenPage, SessionErrorPage, SessionLoadingPage } from '@/pages/AccessPages';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { AprovacoesPage } from '@/pages/AprovacoesPage';
import { AreasPage } from '@/pages/AreasPage';
import { DashboardAreaPage } from '@/pages/DashboardAreaPage';
import { DashboardExecutivoPage } from '@/pages/DashboardExecutivoPage';
import { DashboardPerspectivaPage } from '@/pages/DashboardPerspectivaPage';
import { CenariosPage } from '@/pages/CenariosPage';
import { ImportacoesPage } from '@/pages/ImportacoesPage';
import { AuditoriaPage } from '@/pages/AuditoriaPage';
import { RelatoriosPage } from '@/pages/RelatoriosPage';
import { LongoPrazoPage, ProjecoesPage } from '@/pages/LongoPrazoPage';
import { OrcamentoPage } from '@/pages/OrcamentoPage';
import { CentralEmpresasPage } from '@/pages/CentralEmpresasPage';
import { IndicadoresSucessoPage } from '@/pages/IndicadoresSucessoPage';
import { DiagnosticoPage } from '@/pages/DiagnosticoPage';
import { MatrizPriorizacaoPage } from '@/pages/MatrizPriorizacaoPage';
import { DesdobramentoPage } from '@/pages/DesdobramentoPage';
import { DoisFatoresPage } from '@/pages/DoisFatoresPage';
import { ConstrucaoBscPage } from '@/pages/ConstrucaoBscPage';
import { KpisPage } from '@/pages/KpisPage';
import { MapaEstrategicoPage } from '@/pages/MapaEstrategicoPage';
import { MetasPage } from '@/pages/MetasPage';
import { ModelosPage } from '@/pages/ModelosPage';
import { ParametrosPage } from '@/pages/ParametrosPage';
import { PerformancePage } from '@/pages/PerformancePage';
import { PeriodosPage } from '@/pages/PeriodosPage';
import { PorUnidadePage } from '@/pages/PorUnidadePage';
import { PrevistoRealizadoPage } from '@/pages/PrevistoRealizadoPage';
import { HistoricoIndicadorPage } from '@/pages/HistoricoIndicadorPage';
import { IniciativasPage } from '@/pages/IniciativasPage';
import { PlanosAcaoPage } from '@/pages/PlanosAcaoPage';
import { CentralAlertasPage } from '@/pages/CentralAlertasPage';
import { ResultadosPage } from '@/pages/ResultadosPage';
import { ObjetivoDetalhePage } from '@/pages/ObjetivoDetalhePage';
import { ObjetivosPage } from '@/pages/ObjetivosPage';
import { PerspectivasPage } from '@/pages/PerspectivasPage';
import { PlanejamentoPage } from '@/pages/PlanejamentoPage';
import { WorkspaceHeader, workspaceOrgFromPath } from '@/features/workspace/workspace';
import { EmpresaPage } from '@/pages/EmpresaPage';
import { EquipeFluxusPage } from '@/pages/EquipeFluxusPage';
import { NotFoundPage, RouteErrorPage } from '@/pages/ErrorPages';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { SsoPage } from '@/pages/SsoPage';
import { SsoReturnPage } from '@/pages/SsoReturnPage';
import { SessoesAtivasPage } from '@/pages/SessoesAtivasPage';
import { LoginPage } from '@/pages/LoginPage';
import { NovaEmpresaPage } from '@/pages/NovaEmpresaPage';
import { PerfisEquipePage } from '@/pages/PerfisEquipePage';
import { PerfisPage } from '@/pages/PerfisPage';
import { NotificacoesPage } from '@/pages/NotificacoesPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ReunioesPage } from '@/pages/ReunioesPage';
import { SystemStatusPage } from '@/pages/SystemStatusPage';
import { UsuariosPage } from '@/pages/UsuariosPage';
import { VisaoGeralPage } from '@/pages/VisaoGeralPage';
import { NOTIFICATIONS_PATH, NotificationBell } from '@/features/notifications/NotificationBell';
import { GlobalSearch } from '@/features/search/GlobalSearch';
import type { NavItem } from './navigation';

export type AppPage = NavItem & {
  element: ReactNode;
  /** Módulo do PRD §10 exigido para ver a tela (e o item de menu). Sem módulo: todo usuário logado. */
  module?: ModuleKey;
  /** Telas só da equipe Fluxus ou só das empresas clientes. */
  audience?: 'fluxus' | 'client';
};

/**
 * Telas do app com o item de menu correspondente. O menu é montado a partir desta lista:
 * só aparece o que tem rota de verdade e que o perfil do usuário pode acessar.
 */
export const APP_PAGES: AppPage[] = [
  {
    to: '/empresas',
    label: 'Central de Empresas',
    icon: Building2,
    group: 'overview',
    module: 'central-empresas',
    audience: 'fluxus',
    mobilePrimary: true,
    element: <CentralEmpresasPage />,
  },
  {
    // Indicadores de sucesso do produto (T-205b, RF-51; DEC-071): equipe Fluxus com a Central de Empresas.
    to: '/indicadores-sucesso',
    label: 'Indicadores de sucesso',
    icon: Trophy,
    group: 'overview',
    module: 'central-empresas',
    audience: 'fluxus',
    element: <IndicadoresSucessoPage />,
  },
  {
    to: '/visao-geral',
    label: 'Visão Geral',
    icon: LayoutDashboard,
    group: 'overview',
    module: 'visao-geral',
    audience: 'client',
    mobilePrimary: true,
    element: <VisaoGeralPage />,
  },
  {
    to: '/configuracoes/equipe',
    label: 'Equipe Fluxus',
    icon: UsersRound,
    group: 'settings',
    module: 'administracao',
    audience: 'fluxus',
    element: <EquipeFluxusPage />,
  },
  {
    to: '/configuracoes/perfis',
    label: 'Perfis da equipe',
    icon: ShieldCheck,
    group: 'settings',
    module: 'administracao',
    audience: 'fluxus',
    element: <PerfisEquipePage />,
  },
  {
    to: '/configuracoes/status',
    label: 'Status do sistema',
    icon: Activity,
    group: 'settings',
    module: 'administracao',
    element: <SystemStatusPage />,
  },
  {
    // Modelos de projeto (T-204b, RF-50; DEC-070): equipe Fluxus vê; Admin Fluxus renomeia e exclui.
    to: '/configuracoes/modelos',
    label: 'Modelos de projeto',
    icon: LayoutTemplate,
    group: 'settings',
    module: 'central-empresas',
    audience: 'fluxus',
    element: <ModelosPage />,
  },
  {
    // Minha conta: cada usuário cuida das próprias sessões (RNF-02).
    to: '/configuracoes/minha-conta/sessoes',
    label: 'Sessões ativas',
    icon: MonitorSmartphone,
    group: 'settings',
    element: <SessoesAtivasPage />,
  },
  {
    // Minha conta: verificação em duas etapas (RF-03).
    to: '/configuracoes/minha-conta/dois-fatores',
    label: 'Verificação em duas etapas',
    icon: KeyRound,
    group: 'settings',
    element: <DoisFatoresPage />,
  },
];

export function pageAllowed(page: AppPage, user: Pick<AuthUser, 'permissions' | 'organization'>): boolean {
  if (page.module && !canAccess(user, page.module)) return false;
  if (page.audience === 'fluxus') return user.organization.isFluxus;
  if (page.audience === 'client') return !user.organization.isFluxus;
  return true;
}

/**
 * Telas fora do menu (detalhes, formulários), cada uma com a permissão exigida — a mesma que a API
 * confere nas rotas que a tela chama.
 */
export type InnerPage = {
  path: string;
  module: ModuleKey;
  action: ActionKey;
  audience?: 'fluxus' | 'client';
  element: ReactNode;
};

export const INNER_PAGES: InnerPage[] = [
  // Central de Empresas (PRD §11.2): criar empresa e abrir/editar cliente.
  {
    path: '/empresas/nova',
    module: 'central-empresas',
    action: 'create',
    audience: 'fluxus',
    element: <NovaEmpresaPage />,
  },
  {
    path: '/empresas/:organizationId',
    module: 'central-empresas',
    action: 'view',
    audience: 'fluxus',
    element: <EmpresaPage />,
  },
  {
    path: '/empresas/:organizationId/areas',
    module: 'administracao',
    action: 'view',
    audience: 'fluxus',
    element: <AreasPage />,
  },
  {
    path: '/empresas/:organizationId/usuarios',
    module: 'administracao',
    action: 'view',
    audience: 'fluxus',
    element: <UsuariosPage />,
  },
  {
    path: '/empresas/:organizationId/perfis',
    module: 'administracao',
    action: 'view',
    audience: 'fluxus',
    element: <PerfisPage />,
  },
  // Login único (SSO) da empresa (T-028c, RF-04; DEC-072).
  {
    path: '/empresas/:organizationId/sso',
    module: 'administracao',
    action: 'view',
    audience: 'fluxus',
    element: <SsoPage />,
  },
];

/**
 * Telas do produto no espaço de uma empresa (/empresas/:organizationId/...): o cliente vê as da
 * própria empresa; a equipe Fluxus, as da empresa em que entrou pela Central. O item de menu aparece
 * quando o perfil tem o módulo (a API confere de novo, inclusive o vínculo com a empresa).
 */
export type WorkspacePage = Omit<NavItem, 'to'> & {
  /** Caminho dentro da empresa, sem barra inicial. */
  path: string;
  module: ModuleKey;
  /** Ação exigida no módulo (padrão: "visualizar"). */
  action?: ActionKey;
  /** Só para pessoas das empresas clientes (ex.: aprovar decisões da própria empresa). */
  audience?: 'client';
  element: ReactNode;
};

/** A tela do espaço da empresa está liberada para o perfil (módulo, ação e público). */
export function workspacePageAllowed(
  page: { module: ModuleKey; action?: ActionKey; audience?: 'client' },
  user: Pick<AuthUser, 'permissions' | 'organization'>,
): boolean {
  if (page.audience === 'client' && user.organization.isFluxus) return false;
  return can(user, page.module, page.action ?? 'view');
}

/** Telas de detalhe no espaço da empresa, fora do menu (mesma checagem de empresa e módulo). */
export const WORKSPACE_DETAIL_PAGES: { path: string; module: ModuleKey; element: ReactNode }[] = [
  // Detalhe do objetivo (T-046b, RF-19; DEC-052).
  { path: 'estrategia/objetivos/:objectiveId', module: 'objetivos', element: <ObjetivoDetalhePage /> },
];

export const WORKSPACE_PAGES: WorkspacePage[] = [
  {
    // Dashboard executivo (T-141b, RF-34; DEC-055).
    path: 'dashboards/executivo',
    label: 'Dashboard executivo',
    icon: PresentationIcon,
    group: 'overview',
    module: 'dashboard-executivo',
    element: <DashboardExecutivoPage />,
  },
  {
    // Dashboard por perspectiva (T-142b, RF-35; DEC-056).
    path: 'dashboards/perspectiva',
    label: 'Por perspectiva',
    icon: Layers,
    group: 'overview',
    module: 'dashboard-perspectiva',
    element: <DashboardPerspectivaPage />,
  },
  {
    // Dashboard por área (T-143b, RF-36, RN-24; DEC-057).
    path: 'dashboards/area',
    label: 'Por área',
    icon: Network,
    group: 'overview',
    module: 'dashboard-area',
    element: <DashboardAreaPage />,
  },
  {
    // Diagnóstico empresarial (T-160b, RF-10, RF-11; DEC-061).
    path: 'diagnostico',
    label: 'Diagnóstico empresarial',
    icon: ClipboardList,
    group: 'diagnosis',
    module: 'diagnostico',
    element: <DiagnosticoPage />,
  },
  {
    // Matriz de priorização (T-161b, RF-12, RF-13, RN-25; DEC-062).
    path: 'diagnostico/matriz',
    label: 'Matriz de priorização',
    icon: Grid3x3,
    group: 'diagnosis',
    module: 'priorizacao',
    element: <MatrizPriorizacaoPage />,
  },
  {
    path: 'estrategia/planejamento',
    label: 'Planejamento',
    icon: Compass,
    group: 'strategy',
    module: 'planejamento-estrategico',
    element: <PlanejamentoPage />,
  },
  {
    path: 'estrategia/mapa',
    label: 'Mapa estratégico',
    icon: Waypoints,
    group: 'strategy',
    module: 'mapa-estrategico',
    element: <MapaEstrategicoPage />,
  },
  {
    path: 'estrategia/perspectivas',
    label: 'Perspectivas',
    icon: Layers,
    group: 'strategy',
    module: 'planejamento-estrategico',
    element: <PerspectivasPage />,
  },
  {
    path: 'estrategia/objetivos',
    label: 'Objetivos',
    icon: Target,
    group: 'strategy',
    module: 'objetivos',
    element: <ObjetivosPage />,
  },
  {
    path: 'estrategia/construcao',
    label: 'Construção do BSC',
    icon: LayoutGrid,
    group: 'strategy',
    module: 'objetivos',
    element: <ConstrucaoBscPage />,
  },
  {
    path: 'estrategia/desdobramento',
    label: 'Desdobramento',
    icon: Network,
    group: 'strategy',
    module: 'objetivos',
    element: <DesdobramentoPage />,
  },
  {
    path: 'indicadores/kpis',
    label: 'KPIs',
    icon: Gauge,
    group: 'indicators',
    module: 'indicadores',
    element: <KpisPage />,
  },
  {
    path: 'indicadores/metas',
    label: 'Metas',
    icon: Flag,
    group: 'indicators',
    module: 'metas-resultados',
    element: <MetasPage />,
  },
  {
    path: 'indicadores/resultados',
    label: 'Resultados',
    icon: ClipboardCheck,
    group: 'indicators',
    module: 'metas-resultados',
    element: <ResultadosPage />,
  },
  {
    path: 'indicadores/unidades',
    label: 'Por unidade',
    icon: Store,
    group: 'indicators',
    module: 'metas-resultados',
    element: <PorUnidadePage />,
  },
  {
    // Planejamento de cenários (T-180b, RF-28, RF-14; DEC-063).
    path: 'planejamento/cenarios',
    label: 'Cenários',
    icon: GitBranch,
    group: 'planning',
    module: 'cenarios',
    element: <CenariosPage />,
  },
  {
    // Planejamento de longo prazo e projeções (T-181b, RF-29, RF-26; DEC-064).
    path: 'planejamento/projecoes',
    label: 'Projeções',
    icon: TrendingUp,
    group: 'planning',
    module: 'previsto-realizado',
    element: <ProjecoesPage />,
  },
  {
    path: 'planejamento/longo-prazo',
    label: 'Longo prazo',
    icon: CalendarRange,
    group: 'planning',
    module: 'metas-resultados',
    element: <LongoPrazoPage />,
  },
  {
    // Orçamento — a lógica da planilha (T-182b, RF-30; DEC-065).
    path: 'planejamento/orcamento',
    label: 'Orçamento',
    icon: Wallet,
    group: 'planning',
    module: 'previsto-realizado',
    element: <OrcamentoPage />,
  },
  {
    path: 'indicadores/previsto-realizado',
    label: 'Previsto x Realizado',
    icon: ChartLine,
    group: 'indicators',
    module: 'previsto-realizado',
    element: <PrevistoRealizadoPage />,
  },
  {
    // Central de importação Excel/CSV (T-200c, RF-45, RN-18, RN-19; DEC-066).
    path: 'indicadores/importacoes',
    label: 'Importações',
    icon: FileSpreadsheet,
    group: 'indicators',
    module: 'importacoes',
    element: <ImportacoesPage />,
  },
  {
    path: 'indicadores/historico',
    label: 'Histórico',
    icon: HistoryIcon,
    group: 'indicators',
    module: 'metas-resultados',
    element: <HistoricoIndicadorPage />,
  },
  {
    path: 'execucao/iniciativas',
    label: 'Iniciativas',
    icon: Rocket,
    group: 'execution',
    module: 'iniciativas',
    element: <IniciativasPage />,
  },
  {
    path: 'execucao/planos-acao',
    label: 'Planos de ação',
    icon: ListChecks,
    group: 'execution',
    module: 'planos-acao',
    element: <PlanosAcaoPage />,
  },
  {
    path: 'gestao/alertas',
    label: 'Alertas',
    icon: BellRing,
    group: 'management',
    module: 'alertas',
    element: <CentralAlertasPage />,
  },
  {
    // Gestão › Análise crítica (T-280; RF-49, PRD §21): as reuniões de análise crítica e a condução (a análise crítica
    // inclui visualizar Reuniões — DEC-087).
    path: 'gestao/analise-critica',
    label: 'Análise crítica',
    icon: FileSearch,
    group: 'management',
    module: 'analise-critica',
    // A mesma tela de Reuniões: a chave a remonta ao trocar de item no menu (o filtro de tipo não passa de uma para a outra).
    element: <ReunioesPage key="analise-critica" analysisOnly />,
  },
  {
    path: 'gestao/reunioes',
    label: 'Reuniões',
    icon: CalendarClock,
    group: 'management',
    module: 'reunioes-decisoes',
    element: <ReunioesPage key="reunioes" />,
  },
  {
    // Aprovação de decisões (T-123b, RF-48; DEC-053).
    path: 'gestao/aprovacoes',
    label: 'Aprovações',
    icon: Stamp,
    group: 'management',
    module: 'reunioes-decisoes',
    action: 'approve',
    audience: 'client',
    element: <AprovacoesPage />,
  },
  {
    // Relatórios e exportação PDF/Excel/CSV (T-201d, RF-44, RN-24; DEC-068).
    path: 'relatorios',
    label: 'Relatórios',
    icon: FileBarChart,
    group: 'reports',
    module: 'relatorios',
    action: 'export',
    element: <RelatoriosPage />,
  },
  {
    // Parâmetros da empresa num lugar só (T-204c, RF-50, RN-03; DEC-070).
    path: 'configuracoes/parametros',
    label: 'Parâmetros da empresa',
    icon: Settings2,
    group: 'settings',
    module: 'planejamento-estrategico',
    element: <ParametrosPage />,
  },
  {
    path: 'configuracoes/performance',
    label: 'Performance e semáforo',
    icon: SlidersHorizontal,
    group: 'settings',
    module: 'planejamento-estrategico',
    element: <PerformancePage />,
  },
  {
    path: 'configuracoes/periodos',
    label: 'Fechamento de períodos',
    icon: CalendarCheck,
    group: 'settings',
    module: 'metas-resultados',
    element: <PeriodosPage />,
  },
  {
    // Trilha de auditoria da empresa e histórico por registro (T-202b, RF-46, RN-16; DEC-067).
    path: 'configuracoes/auditoria',
    label: 'Auditoria e histórico',
    icon: FileClock,
    group: 'settings',
    module: 'auditoria',
    element: <AuditoriaPage />,
  },
];

export const workspacePath = (organizationId: string, path: string) => `/empresas/${organizationId}/${path}`;

/** Empresa do espaço de trabalho: a própria (cliente) ou a do caminho (equipe Fluxus). */
export function workspaceOrgFor(user: Pick<AuthUser, 'organization'>, pathname: string): string | null {
  if (!user.organization.isFluxus) return user.organization.id;
  return workspaceOrgFromPath(pathname);
}

export function workspaceItemsFor(
  user: Pick<AuthUser, 'permissions' | 'organization'>,
  organizationId: string | null,
): NavItem[] {
  if (!organizationId) return [];
  return WORKSPACE_PAGES.filter((p) => workspacePageAllowed(p, user)).map((p) => ({
    to: workspacePath(organizationId, p.path),
    label: p.label,
    icon: p.icon,
    group: p.group,
    mobilePrimary: p.mobilePrimary,
  }));
}

export function innerPageAllowed(
  page: InnerPage,
  user: Pick<AuthUser, 'permissions' | 'organization'>,
): boolean {
  if (!can(user, page.module, page.action)) return false;
  if (page.audience === 'fluxus') return user.organization.isFluxus;
  if (page.audience === 'client') return !user.organization.isFluxus;
  return true;
}

export const pagesFor = (user: Pick<AuthUser, 'permissions' | 'organization'>) =>
  APP_PAGES.filter((p) => pageAllowed(p, user));

export const LOGIN_PATH = '/entrar';

/** Rotas que exigem sessão: sem sessão, vai para o login e volta depois para onde estava. */
function ProtectedLayout() {
  const { state, retry, logout } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') return <SessionLoadingPage />;
  if (state.status === 'error') return <SessionErrorPage error={state.error} onRetry={retry} />;
  if (state.status === 'anonymous') {
    const back = `${location.pathname}${location.search}`;
    const search = back === '/' || state.loggedOut ? '' : `?voltar=${encodeURIComponent(back)}`;
    return <Navigate to={`${LOGIN_PATH}${search}`} replace />;
  }

  const user = state.session.user;
  const workspaceOrg = workspaceOrgFor(user, location.pathname);
  // Ao sair, a sessão muda e este layout leva ao login.
  return <Shell user={user} workspaceOrg={workspaceOrg} onLogout={logout} />;
}

/** Menu com o contador de alertas abertos da empresa no item "Alertas" (T-111; DEC-046). */
function Shell({
  user,
  workspaceOrg,
  onLogout,
}: {
  user: AuthUser;
  workspaceOrg: string | null;
  onLogout: () => Promise<void>;
}) {
  const { client } = useAuth();
  const seesAlerts = Boolean(workspaceOrg) && canAccess(user, 'alertas');
  const openAlerts = useQuery({
    queryKey: ['alerts', 'open-count', workspaceOrg],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${workspaceOrg}/alerts/open-count`, {
        schema: alertOpenCountSchema,
        signal,
      }),
    enabled: seesAlerts,
    refetchInterval: 60_000,
    // Falha do contador não atrapalha o menu: o item aparece sem o número.
    meta: { handlesError: true },
  });
  const alertsPath = workspaceOrg ? workspacePath(workspaceOrg, 'gestao/alertas') : null;
  const open = seesAlerts ? (openAlerts.data?.open ?? 0) : 0;
  const items = [...pagesFor(user), ...workspaceItemsFor(user, workspaceOrg)].map((item) =>
    item.to === alertsPath && open > 0
      ? { ...item, badge: { count: open, label: open === 1 ? '1 alerta aberto' : `${open} alertas abertos` } }
      : item,
  );
  return (
    <AppShell
      items={items}
      sidebarHeader={
        user.organization.isFluxus && workspaceOrg ? <WorkspaceHeader organizationId={workspaceOrg} /> : null
      }
      user={user}
      onLogout={onLogout}
      headerActions={
        <>
          {workspaceOrg ? <GlobalSearch organizationId={workspaceOrg} /> : null}
          <NotificationBell />
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}

function PageGate({ page }: { page: AppPage }) {
  const user = useCurrentUser();
  if (!pageAllowed(page, user)) return <ForbiddenPage homePath={homePathFor(user)} />;
  return page.element;
}

function WorkspaceGate({
  page,
}: {
  page: { module: ModuleKey; action?: ActionKey; audience?: 'client'; element: ReactNode };
}) {
  const user = useCurrentUser();
  const location = useLocation();
  const organizationId = workspaceOrgFromPath(location.pathname);
  // Cliente só trabalha na própria empresa; o módulo precisa estar liberado no perfil.
  const ownOrOperator = user.organization.isFluxus || organizationId === user.organization.id;
  if (!ownOrOperator || !workspacePageAllowed(page, user)) {
    return <ForbiddenPage homePath={homePathFor(user)} />;
  }
  return page.element;
}

function InnerGate({ page }: { page: InnerPage }) {
  const user = useCurrentUser();
  if (!innerPageAllowed(page, user)) return <ForbiddenPage homePath={homePathFor(user)} />;
  return page.element;
}

function HomeRedirect() {
  const user = useCurrentUser();
  return <Navigate to={homePathFor(user)} replace />;
}

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: LOGIN_PATH, element: <LoginPage /> },
      // Volta do login pelo SSO da empresa (T-028d): troca o código de retorno pela sessão.
      { path: SSO_RETURN_PATH, element: <SsoReturnPage /> },
      // Recuperação de senha (RF-02): telas sem sessão.
      { path: '/esqueci-senha', element: <ForgotPasswordPage /> },
      { path: '/redefinir-senha', element: <ResetPasswordPage /> },
      // Convite (RF-07): a pessoa convidada cria a senha antes de ter sessão.
      { path: '/convite', element: <AcceptInvitePage /> },
      {
        element: <ProtectedLayout />,
        children: [
          { index: true, element: <HomeRedirect /> },
          ...APP_PAGES.map((p) => ({ path: p.to, element: <PageGate page={p} /> })),
          ...INNER_PAGES.map((p) => ({ path: p.path, element: <InnerGate page={p} /> })),
          ...WORKSPACE_PAGES.map((p) => ({
            path: workspacePath(':organizationId', p.path),
            element: <WorkspaceGate page={p} />,
          })),
          ...WORKSPACE_DETAIL_PAGES.map((p) => ({
            path: workspacePath(':organizationId', p.path),
            element: <WorkspaceGate page={p} />,
          })),
          // Central de notificações (T-113d, RF-42): de cada pessoa, para todos os perfis.
          { path: NOTIFICATIONS_PATH, element: <NotificacoesPage /> },
          { path: '/sem-acesso', element: <ForbiddenPage homePath={null} /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

export const createAppRouter = () => createBrowserRouter(routes);
